import { errorToString } from '@/lib/errors'
import { log } from '@/lib/logger'
import type { Addresses } from '@/types/pbSchema'
import { formatAddress, isAddressBlank } from './address'
import { GOOGLE_MAPS_API_KEY } from './config'

export interface GeocodeResult {
    latitude: number
    longitude: number
    formattedAddress: string
}

export interface GeocodeError {
    error: string
    status: string
}

export async function geocodeAddressString(addressString: string): Promise<GeocodeResult | null> {
    if (!addressString.trim()) {
        return null
    }

    try {
        const url = new URL('https://maps.googleapis.com/maps/api/geocode/json')
        url.searchParams.set('address', addressString.trim())
        url.searchParams.set('key', GOOGLE_MAPS_API_KEY)

        log.debug(`Geocoding address string: ${addressString}`)

        const response = await fetch(url.toString())
        const data = await response.json()

        if (data.status === 'OK' && data.results && data.results.length > 0) {
            const result = data.results[0]
            const { lat, lng } = result.geometry.location

            log.info(`Successfully geocoded address to: ${lat}, ${lng}`)

            return {
                latitude: lat,
                longitude: lng,
                formattedAddress: result.formatted_address,
            }
        }

        log.warn(`Geocoding failed with status: ${data.status}`, {
            address: addressString,
            status: data.status,
        })

        return null
    } catch (error) {
        log.errorWithoutSentry('geocoding address string:', errorToString(error))
        return null
    }
}

export async function geocodeAddress(
    address: Omit<Addresses, 'id' | 'created' | 'updated' | 'org'>
): Promise<GeocodeResult | null> {
    if (isAddressBlank(address)) {
        return null
    }

    const addressString = formatAddress(address)

    try {
        const url = new URL('https://maps.googleapis.com/maps/api/geocode/json')
        url.searchParams.set('address', addressString)
        url.searchParams.set('key', GOOGLE_MAPS_API_KEY)

        log.debug(`Geocoding address: ${addressString}`)

        const response = await fetch(url.toString())
        const data = await response.json()

        if (data.status === 'OK' && data.results && data.results.length > 0) {
            const result = data.results[0]
            const { lat, lng } = result.geometry.location

            log.info(`Successfully geocoded address to: ${lat}, ${lng}`)

            return {
                latitude: lat,
                longitude: lng,
                formattedAddress: result.formatted_address,
            }
        }

        log.warn(`Geocoding failed with status: ${data.status}`, {
            address: addressString,
            status: data.status,
        })

        return null
    } catch (error) {
        log.errorWithoutSentry('geocoding address:', errorToString(error))
        return null
    }
}
